import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import StandardPagination from './StandardPagination';
import useImpersonation from '../hooks/useImpersonation';
import ImpersonationModal from './ImpersonationModal';
import styles from './AllUsers.module.css';

const AllUsers = ({ users, searchQuery, setSearchQuery, handleSearch, handlePageChange, currentPage, totalPages, itemsPerPage, onItemsPerPageChange, totalItems }) => {
  const navigate = useNavigate();
  const { startImpersonation, loading } = useImpersonation();
  const [impersonationModal, setImpersonationModal] = useState({ show: false, user: null });

  const handleImpersonateClick = (user) => {
    setImpersonationModal({ show: true, user });
  };

  const handleImpersonateConfirm = async () => {
    const { user } = impersonationModal;
    if (!user) return;

    // Determine user type
    let userType = 'teacher';
    if (user.isSchAdmin) {
      userType = 'schoolAdmin';
    } else if (user.isOrgAdmin) {
      userType = 'organization';
    } else if (user.studentCode) {
      userType = 'student';
    } else if (user.isAdmin) {
      userType = 'admin';
    }

    const result = await startImpersonation(user._id, userType);
    if (!result.success) {
      alert(result.error || 'Failed to start impersonation');
      setImpersonationModal({ show: false, user: null });
    }
  };

  const handleImpersonateCancel = () => {
    setImpersonationModal({ show: false, user: null });
  };

  return (
      <div className={styles.usersTabContent}>
        <div className="container">
          <div>
            {/* Search Bar - At the Top */}
            <div className="d-flex justify-content-end mb-3">
              <form onSubmit={handleSearch} className="form-inline">
                <input
                  className="form-control mr-sm-2"
                  type="search"
                  name="search"
                  placeholder="Users Email PostCode"
                  aria-label="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoComplete="off"
                />
                <button className="btn btn-outline-success my-2 my-sm-0" type="submit">
                  Search
                </button>
              </form>
            </div>
            
            {/* Items Per Page Controls - Below Search */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="d-flex align-items-center">
                  <label htmlFor="usersPerPage" className="form-label me-2 mb-0">Users per page:</label>
                  <select 
                    id="usersPerPage"
                    className="form-select"
                    value={itemsPerPage || 12}
                    onChange={(e) => onItemsPerPageChange && onItemsPerPageChange(parseInt(e.target.value))}
                    style={{ 
                      maxWidth: '80px', 
                      height: '32px',
                      fontSize: '14px',
                      padding: '4px 8px'
                    }}
                  >
                    {[10, 20, 50, 100].map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-6 text-end">
                <small style={{ color: 'var(--darkOrange)' }}>
                  Showing {((currentPage - 1) * (itemsPerPage || 12)) + 1} to {Math.min(currentPage * (itemsPerPage || 12), totalItems || users.length)} of {totalItems || users.length} users
                </small>
              </div>
            </div>
          <div className={styles.cardGrid}>
            {users.map(user => (
              <div key={user._id} className={styles.cardItem}>
                <div className={styles.card}>
                  <div className={styles.cardBody}>
                    <div className={styles.cardContent}>
                      <div className={styles.cardInfo}>
                        <h5 className={styles.cardTitle}>{user.contactName}</h5>
                        <p className={styles.cardSubtitle} title={user.schoolName}>School: {user.schoolName}</p>
                        <p className={styles.cardSubtitle} title={user.email}>Email: {user.email}</p>
                        <p className={styles.cardSubtitle} title={user.tchCode || user.teacherCode || 'Not assigned'}>Teacher Code: {user.tchCode || user.teacherCode || 'Not assigned'}</p>
                      </div>
                      <div className={styles.cardButton}>
                        <Button 
                          className={`btn ${styles.btnPrimary} ${styles.btnLeft}`}
                          onClick={() => navigate(`/user/${user._id}`)}
                        >
                          View<br/>Details
                        </Button>
                        <Button 
                          className={`btn ${styles.btnWarning} ${styles.btnRight}`}
                          onClick={() => handleImpersonateClick(user)}
                          title="Impersonate User"
                          disabled={loading}
                        >
                          Impersonate<br/>User
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <StandardPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage || 12}
            onItemsPerPageChange={onItemsPerPageChange}
            totalItems={totalItems || users.length}
            itemsPerPageLabel="Users per page:"
            pageInfoLabel="Showing"
            showItemsPerPage={false}
          />
          <br />
        </div>
      </div>

      <ImpersonationModal
        show={impersonationModal.show}
        onClose={handleImpersonateCancel}
        onConfirm={handleImpersonateConfirm}
        user={impersonationModal.user}
        loading={loading}
      />
    </div>
  );
};

export default AllUsers; 